import React, { useEffect, useState, useCallback } from "react";
import {
  editLeaveApproval,
  leaveApprovalList,
  leaveApprovalSummaryList,
} from "../../Api/ApprovalApi";
import { checkStatusCodeSuccess, handleArrayResponse, handleResponse } from "../../Constant/common";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import { approvalLabel } from "../../Constant/HR/attendanceApproval";
import { useSelectedCenter } from "../CenterContext";
import BaseModal from "../../BaseComponents/BaseModal";
import { notFound } from "../../Constant";
import { toast } from "react-toastify";
import { leaveApprovalTitle } from "../../Constant/title";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { MONTH_NAMES } from "../../utils/dateUtils";
import {
  CheckCircle,
  XCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  User,
  FileText,
  AlertCircle,
  CheckSquare,
} from "react-feather";

/* ── Status config ── */
const STATUS_CFG = {
  [approvalLabel.Pending]: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.25)",
  },
  [approvalLabel.Approved]: {
    color: "#10b981",
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.25)",
  },
  [approvalLabel.Rejected]: {
    color: "#f43f5e",
    bg: "rgba(244,63,94,0.12)",
    border: "rgba(244,63,94,0.25)",
  },
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#10b981,#06b6d4)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#6366f1)",
];

/* ── Detail row inside modal ── */
const DetailRow = ({ icon: Icon, label, value, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 10,
      background: "var(--icon-btn-bg)",
      border: "1px solid var(--glass-border)",
    }}
  >
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        flexShrink: 0,
        background: color ? `${color}15` : "rgba(99,102,241,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon size={14} strokeWidth={2} style={{ color: color || "var(--brand-indigo)" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: "var(--text-muted)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "var(--text-primary)",
          wordBreak: "break-word",
        }}
      >
        {value || handleResponse.nullData}
      </div>
    </div>
  </div>
);

const LeaveApproval = () => {
  const [groupedLeaveList, setGroupedLeaveList] = useState({});
  const [expandedSections, setExpandedSections] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [monthPagingInfo, setMonthPagingInfo] = useState({}); // monthYear -> { pageNumber, hasMore, loading }
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState(null);
  const [modalTargetId, setModalTargetId] = useState(null);
  const [noResult, setNoResult] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState({
    listOfLeave: [],
    totalRecordsCount: 0,
    totalPendingCount: 0,
    totalApprovedCount: 0,
    totalRejectedCount: 0,
  });
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  useEffect(() => {
    document.title = leaveApprovalTitle;
  }, []);

  // Fetch summary data from listOfAllLeaveSummary API
  const fetchSummary = useCallback(() => {
    const payload = {
      ...selectedCenterPayload,
      search: searchQuery || "",
      year: selectedYear,
      pageSize: 10,
      pageNumber: 1,
      order: ["from_date", "desc"],
    };
    leaveApprovalSummaryList(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setSummaryData({
            listOfLeave: resp?.data?.listOfLeave || [],
            totalRecordsCount: resp?.data?.totalRecordsCount || 0,
            totalPendingCount: resp?.data?.totalPendingCount || 0,
            totalApprovedCount: resp?.data?.totalApprovedCount || 0,
            totalRejectedCount: resp?.data?.totalRejectedCount || 0,
          });
        }
      })
      .catch(() => {});
  }, [selectedCenterPayload, searchQuery, selectedYear]);

  // Initialize all 12 months for the selected year and fetch summary
  useEffect(() => {
    const allMonths = {};
    MONTH_NAMES.forEach((month) => {
      allMonths[`${month}-${selectedYear}`] = [];
    });
    setGroupedLeaveList(allMonths);
    setExpandedSections([]);
    setMonthPagingInfo({});
    setNoResult(false);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, fetchSummary]);

  const groupByMonthAndYear = (leaveData) =>
    leaveData?.reduce((acc, leave) => {
      const date = new Date(leave?.from_date);
      const month = MONTH_NAMES[date.getMonth()];
      const year = date.getFullYear();
      const key = `${month}-${year}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(leave);
      return acc;
    }, {});

  const fetchData = useCallback(
    (query, month = null) => {
      setLoading(true);
      const payload = {
        ...selectedCenterPayload,
        search: query || "",
        year: selectedYear,
        pageSize: null,
        pageNumber: 1,
        order: ["from_date", "desc"],
      };
      if (month) {
        payload.month = month;
      }
      leaveApprovalList(payload)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const leaveData = resp?.data?.listOfLeave || [];
            setNoResult(leaveData.length === 0);
            setGroupedLeaveList(groupByMonthAndYear(leaveData));
          } else {
            setNoResult(true);
            setGroupedLeaveList({});
          }
        })
        .catch(() => {
          setNoResult(true);
          setGroupedLeaveList({});
        })
        .finally(() => setLoading(false));
    },
    [selectedCenterPayload, selectedYear, setLoading],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchQuery) {
      fetchData(searchQuery);
    } else {
      // Reset to all 12 months with empty arrays if search is cleared
      const allMonths = {};
      MONTH_NAMES.forEach((month) => {
        allMonths[`${month}-${selectedYear}`] = [];
      });
      setGroupedLeaveList(allMonths);
      setExpandedSections([]);
      setMonthPagingInfo({});
      setNoResult(false);
    }
  }, [searchQuery, selectedYear, fetchData]);

  const fetchMonthPage = useCallback(
    (monthYear, pageNum, isRefresh = false) => {
      const monthName = monthYear.split("-")[0];
      const monthNumber = MONTH_NAMES.indexOf(monthName) + 1;

      setMonthPagingInfo((prev) => ({
        ...prev,
        [monthYear]: {
          ...(prev[monthYear] || { pageNumber: 1, hasMore: true }),
          loading: true,
        },
      }));

      const payload = {
        ...selectedCenterPayload,
        search: searchQuery || "",
        year: selectedYear,
        month: monthNumber,
        pageSize: 10,
        pageNumber: pageNum,
        order: ["from_date", "desc"],
      };

      return leaveApprovalList(payload)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const leaveData = resp?.data?.listOfLeave || [];
            const totalRecordsCount = resp?.data?.totalRecordsCount || 0;

            setMonthPagingInfo((prev) => ({
              ...prev,
              [monthYear]: {
                pageNumber: pageNum,
                hasMore: pageNum * 10 < totalRecordsCount,
                loading: false,
              },
            }));

            setGroupedLeaveList((prev) => {
              const currentList = isRefresh || pageNum === 1 ? [] : prev[monthYear] || [];
              const existingIds = new Set(currentList.map((item) => item.id));
              const newItems = leaveData.filter((item) => !existingIds.has(item.id));
              const updatedList = [...currentList, ...newItems];

              return {
                ...prev,
                [monthYear]: updatedList,
              };
            });
            setNoResult(false);
          } else {
            setMonthPagingInfo((prev) => ({
              ...prev,
              [monthYear]: {
                pageNumber: pageNum,
                hasMore: false,
                loading: false,
              },
            }));
          }
        })
        .catch(() => {
          setMonthPagingInfo((prev) => ({
            ...prev,
            [monthYear]: {
              pageNumber: pageNum,
              hasMore: false,
              loading: false,
            },
          }));
        });
    },
    [selectedCenterPayload, searchQuery, selectedYear],
  );

  const refreshExpandedSections = useCallback(() => {
    if (searchQuery) {
      fetchData(searchQuery);
      return;
    }
    expandedSections.forEach((monthYear) => {
      fetchMonthPage(monthYear, 1, true);
    });
  }, [expandedSections, searchQuery, fetchData, fetchMonthPage]);

  /* ── Approve / Reject ── */
  const editData = (approve) => {
    setBtnLoader(true);
    const ids = modalOpen && modalTargetId ? [modalTargetId] : selectedIds;
    editLeaveApproval({
      leaveIds: ids,
      leave_status: approve ? approvalLabel.Approved : approvalLabel.Rejected,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setSelectedIds([]);
          refreshExpandedSections();
          fetchSummary();
          toast.success(resp?.message);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch(handleArrayResponse)
      .finally(() => {
        setBtnLoader(false);
        setModalOpen(false);
        setModalTargetId(null);
      });
  };

  /* ── Selection helpers ── */
  const handleSelectEntry = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSelectMonth = (monthYear, leaves) => {
    const ids = leaves.filter((e) => e.leave_status === approvalLabel.Pending).map((e) => e.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  };

  const handleSelectAll = () => {
    const allIds = Object.values(groupedLeaveList)
      .flat()
      .filter((e) => e.leave_status === approvalLabel.Pending)
      .map((e) => e.id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const toggleSection = (id) =>
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  // /* ── Pagination ── */
  // const handleYearChange = (e) => {
  //   setSelectedYear(Number(e.target.value));
  //   setExpandedSections([]);
  // };

  const handleMonthClick = (monthYear) => {
    if (!expandedSections.includes(monthYear)) {
      setSelectedIds([]);
      const paging = monthPagingInfo[monthYear];
      if (paging && groupedLeaveList[monthYear] && groupedLeaveList[monthYear].length > 0) {
        toggleSection(monthYear);
      } else {
        setLoading(true);
        fetchMonthPage(monthYear, 1).finally(() => setLoading(false));
        toggleSection(monthYear);
      }
    } else {
      toggleSection(monthYear);
    }
  };

  const handleMonthScroll = (e, monthYear) => {
    const target = e.target;
    const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 20;
    const paging = monthPagingInfo[monthYear] || { pageNumber: 1, hasMore: true, loading: false };

    if (isNearBottom && paging.hasMore && !paging.loading) {
      fetchMonthPage(monthYear, paging.pageNumber + 1);
    }
  };

  const handleSearchChange = useCallback((v) => {
    setSelectedIds([]);
    if (v !== undefined) setSearchQuery(v);
  }, []);

  /* ── Derived ── */
  const allLeaves = Object.values(groupedLeaveList).flat();
  const totalRecords = summaryData.totalRecordsCount;
  const totalPendingCount = summaryData.totalPendingCount;
  const totalApprovedCount = summaryData.totalApprovedCount;
  const totalRejectedCount = summaryData.totalRejectedCount;

  // Build a lookup map from summary for month-wise totalRequests
  const summaryMonthMap = {};
  (summaryData.listOfLeave || []).forEach((item) => {
    // item.month is like "July 2026" -> convert to key "July-2026"
    const parts = item.month?.split(" ");
    if (parts?.length === 2) {
      summaryMonthMap[`${parts[0]}-${parts[1]}`] = item.totalRequests || 0;
    }
  });

  const allPendingIds = allLeaves
    .filter((e) => e.leave_status === approvalLabel.Pending)
    .map((e) => e.id);
  const allSelected =
    allPendingIds.length > 0 && allPendingIds.every((id) => selectedIds.includes(id));
  const hasPending = allPendingIds.length > 0;

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {approvalLabel.LeaveApproval}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Review and action pending leave requests
          </p>
        </div>

        {/* Summary pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Total", value: totalRecords, color: "#6366f1", bg: "rgba(99,102,241,0.1)" },
            {
              label: "Pending",
              value: totalPendingCount,
              color: "#f59e0b",
              bg: "rgba(245,158,11,0.1)",
            },
            {
              label: "Approved",
              value: totalApprovedCount,
              color: "#10b981",
              bg: "rgba(16,185,129,0.1)",
            },
            {
              label: "Rejected",
              value: totalRejectedCount,
              color: "#ef4444",
              bg: "rgba(239,68,68,0.1)",
            },
            {
              label: "Selected",
              value: selectedIds.length,
              color: "#06b6d4",
              bg: "rgba(6,182,212,0.1)",
            },
          ].map(({ label, value, color, bg }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 10,
                background: bg,
                border: `1px solid ${color}30`,
                fontSize: "0.8125rem",
              }}
            >
              <span style={{ fontWeight: 800, color }}>{value}</span>
              <span style={{ color: "var(--text-muted)" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── List ── */}
      <div className="animate-slide-up">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Search bar and year selector (matching CustomPagination layout) */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 150,
              }}
            >
              <BaseDatePicker
                name="yearSelector"
                label=""
                showYearPicker={true}
                value={String(selectedYear)}
                onChange={(val) => {
                  if (val) {
                    const year = parseInt(val);
                    if (!isNaN(year)) {
                      setSelectedYear(year);
                      setExpandedSections([]);
                    }
                  }
                }}
                placeholder="Select year"
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <input
                type="text"
                placeholder="Search…"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--glass-border)",
                  background: "var(--input-bg)",
                  color: "var(--text-primary)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
            </div>
          </div>

          {/* Select-all bar */}
          {hasPending && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderRadius: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-primary)",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    flexShrink: 0,
                    background: allSelected ? "var(--brand-indigo)" : "transparent",
                    border: `1.5px solid ${allSelected ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.15s",
                    boxShadow: allSelected ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                  }}
                >
                  {allSelected && <CheckSquare size={12} strokeWidth={3} color="#fff" />}
                </div>
                {approvalLabel.SelectAll}
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    background: "rgba(245,158,11,0.1)",
                    color: "#f59e0b",
                  }}
                >
                  {allPendingIds.length} pending
                </span>
              </button>
            </div>
          )}

          {/* Month groups */}
          {!noResult ? (
            Object.entries(groupedLeaveList)
              .sort(([a], [b]) => {
                const [monthA, yearA] = a.split("-");
                const [monthB, yearB] = b.split("-");
                const monthIndexA = MONTH_NAMES.indexOf(monthA);
                const monthIndexB = MONTH_NAMES.indexOf(monthB);
                if (yearA !== yearB) return Number(yearA) - Number(yearB);
                return monthIndexA - monthIndexB;
              })
              .map(([monthYear, leaves]) => {
                const isOpen = expandedSections.includes(monthYear);
                const pendingInMonth = leaves.filter(
                  (e) => e.leave_status === approvalLabel.Pending,
                );
                const monthAllSelected =
                  pendingInMonth.length > 0 &&
                  pendingInMonth.every((e) => selectedIds.includes(e.id));
                const monthSomeSelected = pendingInMonth.some((e) => selectedIds.includes(e.id));

                return (
                  <div
                    key={monthYear}
                    style={{
                      background: "var(--glass-bg)",
                      border: "1px solid var(--glass-border)",
                      borderRadius: 16,
                      overflow: "hidden",
                      boxShadow: isOpen ? "0 8px 32px rgba(99,102,241,0.08)" : "none",
                      transition: "box-shadow 0.2s",
                    }}
                  >
                    {/* Month header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 18px",
                        borderBottom: isOpen ? "1px solid var(--glass-border)" : "none",
                        background: isOpen ? "rgba(99,102,241,0.03)" : "transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => handleMonthClick(monthYear)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* Month select-all */}
                        {pendingInMonth.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectMonth(monthYear, leaves);
                            }}
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 6,
                              flexShrink: 0,
                              background: monthAllSelected
                                ? "var(--brand-indigo)"
                                : monthSomeSelected
                                  ? "rgba(99,102,241,0.3)"
                                  : "transparent",
                              border: `1.5px solid ${monthAllSelected || monthSomeSelected ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {monthAllSelected && (
                              <CheckSquare size={11} strokeWidth={3} color="#fff" />
                            )}
                            {!monthAllSelected && monthSomeSelected && (
                              <div
                                style={{ width: 8, height: 2, background: "#fff", borderRadius: 1 }}
                              />
                            )}
                          </button>
                        )}

                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: "rgba(99,102,241,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Calendar
                            size={16}
                            strokeWidth={2}
                            style={{ color: "var(--brand-indigo)" }}
                          />
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: "0.9375rem",
                              color: "var(--text-primary)",
                            }}
                          >
                            {monthYear.split("-")[0]}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                              marginTop: 1,
                            }}
                          >
                            {summaryMonthMap[monthYear] ?? leaves.length} request
                            {(summaryMonthMap[monthYear] ?? leaves.length) !== 1 ? "s" : ""}
                            {pendingInMonth.length > 0 && (
                              <span style={{ marginLeft: 6, color: "#f59e0b", fontWeight: 600 }}>
                                · {pendingInMonth.length} pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div style={{ color: "var(--text-muted)" }}>
                        {isOpen ? (
                          <ChevronUp size={18} strokeWidth={2} />
                        ) : (
                          <ChevronDown size={18} strokeWidth={2} />
                        )}
                      </div>
                    </div>

                    {/* Leave entries */}
                    {isOpen && (
                      <div
                        onScroll={(e) => handleMonthScroll(e, monthYear)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                          maxHeight: "400px",
                          overflowY: "auto",
                        }}
                      >
                        {leaves.map((entry, idx) => {
                          const sc =
                            STATUS_CFG[entry.leave_status] || STATUS_CFG[approvalLabel.Pending];
                          const isPending = entry.leave_status === approvalLabel.Pending;
                          const isChecked = selectedIds.includes(entry.id);
                          const avatarGradient =
                            AVATAR_GRADIENTS[
                              (entry?.authUser?.name?.charCodeAt(0) || idx) %
                                AVATAR_GRADIENTS.length
                            ];

                          return (
                            <div
                              key={entry.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "14px 18px",
                                borderBottom:
                                  idx < leaves.length - 1
                                    ? "1px solid var(--glass-border)"
                                    : "none",
                                background: isChecked ? "rgba(99,102,241,0.04)" : "transparent",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                if (!isChecked)
                                  e.currentTarget.style.background = "var(--nav-hover)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isChecked
                                  ? "rgba(99,102,241,0.04)"
                                  : "transparent";
                              }}
                            >
                              {/* Checkbox */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (isPending) handleSelectEntry(entry.id);
                                }}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 6,
                                  flexShrink: 0,
                                  background: isChecked ? "var(--brand-indigo)" : "transparent",
                                  border: `1.5px solid ${isChecked ? "var(--brand-indigo)" : isPending ? "var(--glass-border)" : "transparent"}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: isPending ? "pointer" : "default",
                                  opacity: isPending ? 1 : 0,
                                  transition: "all 0.15s",
                                  boxShadow: isChecked ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                                }}
                              >
                                {isChecked && (
                                  <CheckSquare size={11} strokeWidth={3} color="#fff" />
                                )}
                              </button>

                              {/* Avatar */}
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 12,
                                  flexShrink: 0,
                                  background: avatarGradient,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 4px 12px rgba(99,102,241,0.2)",
                                }}
                              >
                                <span
                                  style={{ fontSize: "0.8125rem", fontWeight: 800, color: "#fff" }}
                                >
                                  {getInitials(entry?.authUser?.name)}
                                </span>
                              </div>

                              {/* Info */}
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px 20px",
                                  alignItems: "center",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "0.9375rem",
                                    color: "var(--text-primary)",
                                    minWidth: 120,
                                  }}
                                >
                                  {entry?.authUser?.name || handleResponse.nullData}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.8125rem",
                                    color: "var(--text-muted)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <Calendar size={12} strokeWidth={2} />
                                  {entry?.from_date}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.8125rem",
                                    color: "var(--text-muted)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <Clock size={12} strokeWidth={2} />
                                  {entry?.leave_duration} day
                                  {entry?.leave_duration !== 1 ? "s" : ""}
                                </span>
                                <span
                                  style={{
                                    padding: "2px 10px",
                                    borderRadius: 999,
                                    fontSize: "0.6875rem",
                                    fontWeight: 700,
                                    background: "rgba(99,102,241,0.1)",
                                    color: "var(--brand-indigo)",
                                  }}
                                >
                                  {entry?.leave_type}
                                </span>
                                {entry?.description && (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "var(--text-muted)",
                                      maxWidth: 200,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={entry.description}
                                  >
                                    {entry.description}
                                  </span>
                                )}
                              </div>

                              {/* Status + view */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexShrink: 0,
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 12px",
                                    borderRadius: 999,
                                    fontSize: "0.6875rem",
                                    fontWeight: 700,
                                    background: sc.bg,
                                    color: sc.color,
                                    border: `1px solid ${sc.border}`,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {entry.leave_status}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setModalOpen(true);
                                    setModalTargetId(entry.id);
                                    setLeaveDetails(entry);
                                  }}
                                  aria-label={`View ${entry?.authUser?.name}`}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: "rgba(99,102,241,0.08)",
                                    border: "1px solid rgba(99,102,241,0.2)",
                                    color: "var(--brand-indigo)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(99,102,241,0.15)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "rgba(99,102,241,0.08)";
                                  }}
                                >
                                  <Eye size={14} strokeWidth={2} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {monthPagingInfo[monthYear]?.loading && (
                          <div
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              color: "var(--text-muted)",
                              fontSize: "0.8125rem",
                            }}
                          >
                            Loading more...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "60px 20px",
                gap: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 16,
              }}
            >
              <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Leave detail modal ── */}
      <BaseModal
        isOpen={modalOpen}
        title={approvalLabel.LeaveDetails}
        toggler={() => {
          setModalOpen(false);
          setModalTargetId(null);
        }}
        onSecondaryClick={() => editData(false)}
        submitText={approvalLabel.ApproveCurrent}
        closeText={approvalLabel.RejectCurrent}
        submit={() => editData(true)}
        disabled={btnLoader}
        loader={btnLoader}
        size="md"
        hideSubmit={leaveDetails?.leave_status === approvalLabel.Approved}
        hideClose={leaveDetails?.leave_status === approvalLabel.Rejected}
      >
        {leaveDetails && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Status banner */}
            {(() => {
              const sc = STATUS_CFG[leaveDetails.leave_status] || STATUS_CFG[approvalLabel.Pending];
              return (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    marginBottom: 4,
                    background: sc.bg,
                    border: `1px solid ${sc.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {leaveDetails.leave_status === approvalLabel.Approved ? (
                    <CheckCircle size={16} strokeWidth={2} style={{ color: sc.color }} />
                  ) : leaveDetails.leave_status === approvalLabel.Rejected ? (
                    <XCircle size={16} strokeWidth={2} style={{ color: sc.color }} />
                  ) : (
                    <Clock size={16} strokeWidth={2} style={{ color: sc.color }} />
                  )}
                  <span style={{ fontWeight: 700, fontSize: "0.875rem", color: sc.color }}>
                    {leaveDetails.leave_status}
                  </span>
                </div>
              );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <DetailRow
                icon={User}
                label={approvalLabel.Staff}
                value={leaveDetails?.authUser?.name}
                color="#6366f1"
              />
              <DetailRow
                icon={FileText}
                label={approvalLabel.LeaveType}
                value={leaveDetails?.leave_type}
                color="#8b5cf6"
              />
              <DetailRow
                icon={FileText}
                label="Leave Category"
                value={leaveDetails?.leave_category_name}
                color="#ec4899"
              />
              <DetailRow
                icon={Calendar}
                label={approvalLabel.LeaveDuration}
                value={`${leaveDetails?.from_date}`}
                color="#06b6d4"
              />
              <DetailRow
                icon={Clock}
                label={approvalLabel.LeavesAvailed}
                value={`${leaveDetails?.leave_duration} day${leaveDetails?.leave_duration !== 1 ? "s" : ""}`}
                color="#f59e0b"
              />
              <DetailRow
                icon={FileText}
                label={approvalLabel.Description}
                value={leaveDetails?.description}
                color="#64748b"
              />
            </div>

            {leaveDetails?.leave_summary && (
              <DetailRow
                icon={FileText}
                label={approvalLabel.LeaveSummary}
                value={leaveDetails.leave_summary}
                color="#10b981"
              />
            )}
            {leaveDetails?.description && (
              <DetailRow
                icon={FileText}
                label={approvalLabel.Description}
                value={leaveDetails.description}
                color="#64748b"
              />
            )}
          </div>
        )}
      </BaseModal>

      {/* ── Floating action bar (shown when items selected) ── */}
      {selectedIds.length > 0 && (
        <div
          className="animate-slide-up"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderRadius: 16,
            background: "var(--dropdown-bg)",
            border: "1px solid var(--glass-border)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
            backdropFilter: "blur(20px)",
            zIndex: 1000,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "var(--text-primary)" }}>
            {selectedIds.length} selected
          </span>
          <div style={{ width: 1, height: 20, background: "var(--glass-border)" }} />
          <BaseButton
            color="success"
            onClick={() => editData(true)}
            disabled={btnLoader}
            loader={btnLoader}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <CheckCircle size={14} strokeWidth={2} />
            {approvalLabel.Approve}
          </BaseButton>
          <BaseButton
            color="danger"
            onClick={() => editData(false)}
            disabled={btnLoader}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <XCircle size={14} strokeWidth={2} />
            {approvalLabel.Reject}
          </BaseButton>
        </div>
      )}
    </div>
  );
};

export default LeaveApproval;
